import type { LucideIcon } from 'lucide-react';
import { Copy, ListTree, Play, Plus, Trash2 } from 'lucide-react';
import type {
  ActionConstructor,
  ActionPayload,
} from '@cotera/client/v0/actions/framework';
import { WorkflowToolbarAddNodeAction } from './workflow-toolbar-add-node.action';
import { WorkflowToolbarAddTriggerAction } from './workflow-toolbar-add-trigger.action';
import { WorkflowToolbarOpenHierarchyAction } from './workflow-toolbar-open-hierarchy.action';
import { WorkflowToolbarTestWebhookAction } from './workflow-toolbar-test-webhook.action';
import { WorkflowToolbarTriggerCronNowAction } from './workflow-toolbar-trigger-cron-now.action';
import { WorkflowToolbarTriggerEventNowAction } from './workflow-toolbar-trigger-event-now.action';
import { WorkflowToolbarTestDatasetAction } from './workflow-toolbar-test-dataset.action';
import { WorkflowToolbarTriggerWorkflowAction } from './workflow-toolbar-trigger-workflow.action';
import { WorkflowToolbarDeleteSelectionAction } from './workflow-toolbar-delete-selection.action';
import { WorkflowToolbarDuplicateSelectionAction } from './workflow-toolbar-duplicate-selection.action';
import { WORKFLOW_TREE_VIEW_TITLE } from './workflow-artifact-views';

/** The toolbar's buttons, in the order they are laid out. */
export const WORKFLOW_EDITOR_TOOLBAR_ACTIONS = [
  WorkflowToolbarAddNodeAction,
  WorkflowToolbarAddTriggerAction,
  WorkflowToolbarTriggerWorkflowAction,
  WorkflowToolbarOpenHierarchyAction,
  WorkflowToolbarTestWebhookAction,
  WorkflowToolbarTriggerCronNowAction,
  WorkflowToolbarTriggerEventNowAction,
  WorkflowToolbarTestDatasetAction,
  WorkflowToolbarDuplicateSelectionAction,
  WorkflowToolbarDeleteSelectionAction,
] as const;

export type WorkflowEditorToolbarActionConstructor =
  (typeof WORKFLOW_EDITOR_TOOLBAR_ACTIONS)[number];

/**
 * Payload-erased view of a toolbar action constructor.
 *
 * The toolbar treats every action identically — it reads `applicable` and
 * executes with no payload — but the actions don't share one payload type
 * (`add-trigger` carries the trigger wizard's result, the rest are empty).
 * `Action`'s `inputSchema: ZodSchema<P>` is invariant in `P`, so passing the
 * raw union above to `useAction`/`getAction` makes inference pick a single
 * payload and reject every action that doesn't use it.
 */
export type WorkflowEditorToolbarActionRef = ActionConstructor<ActionPayload>;

export const WORKFLOW_TOOLBAR_ACTION_UI: Record<
  WorkflowEditorToolbarActionConstructor['name'],
  { Icon: LucideIcon; label: string; destructive?: boolean }
> = {
  [WorkflowToolbarAddNodeAction.name]: { Icon: Plus, label: 'Add node' },
  [WorkflowToolbarAddTriggerAction.name]: { Icon: Plus, label: 'Add trigger' },
  [WorkflowToolbarOpenHierarchyAction.name]: {
    Icon: ListTree,
    label: WORKFLOW_TREE_VIEW_TITLE,
  },
  [WorkflowToolbarTriggerWorkflowAction.name]: {
    Icon: Play,
    label: 'Trigger workflow',
  },
  [WorkflowToolbarTestWebhookAction.name]: {
    Icon: Play,
    label: 'Test webhook',
  },
  [WorkflowToolbarTriggerCronNowAction.name]: {
    Icon: Play,
    label: 'Trigger now',
  },
  [WorkflowToolbarTriggerEventNowAction.name]: {
    Icon: Play,
    label: 'Trigger now',
  },
  [WorkflowToolbarTestDatasetAction.name]: {
    Icon: Play,
    label: 'Test trigger',
  },
  [WorkflowToolbarDuplicateSelectionAction.name]: {
    Icon: Copy,
    label: 'Duplicate',
  },
  [WorkflowToolbarDeleteSelectionAction.name]: {
    Icon: Trash2,
    label: 'Delete',
    // The only destructive entry in a row of constructive ones, and it used to be
    // styled identically to "Add node".
    destructive: true,
  },
};
